import { ContractTranslate } from './contractTranslate.model';

const contractTranslateEN: ContractTranslate = {
  tab: {
    title: 'Contracts',
  },
  contractor: {
    section: {
      title: 'Contractor',
      description:
        'Register the contractor details (you or your studio) shown on issued contracts.',
      add: 'New contractor',
      empty: 'No contractor registered',
    },
    personType: {
      label: 'Person type',
      PF: 'Individual',
      PJ: 'Company',
    },
    fields: {
      name: 'Name / Company name',
      cpf: 'CPF',
      cnpj: 'CNPJ',
      legalRepresentativeName: 'Legal representative name',
      legalRepresentativeDocument: 'Legal representative CPF',
      email: 'Email',
      phone: 'Phone',
      instagram: 'Instagram',
    },
    modal: {
      titleCreate: 'New contractor',
      titleEdit: 'Edit contractor',
      save: 'Save',
      cancel: 'Cancel',
    },
    actions: {
      edit: 'Edit contractor',
      delete: 'Delete contractor',
    },
    delete: {
      title: 'Delete contractor',
      text: 'Are you sure you want to delete the contractor "{{name}}"?',
      secondaryText:
        'Contracts already issued with this contractor will not be changed.',
      confirm: 'Delete',
      cancel: 'Cancel',
    },
    toast: {
      created: 'Contractor created',
      updated: 'Contractor updated',
      deleted: 'Contractor deleted',
      error: {
        create: 'Could not create the contractor. Please try again',
        update: 'Could not update the contractor. Please try again',
        delete: 'Could not delete the contractor. Please try again',
      },
    },
    validation: {
      nameRequired: 'Enter the contractor name',
      nameMaxLength: 'Name must be at most 100 characters',
      documentCpfInvalid: 'Enter a valid CPF with 11 digits',
      documentCnpjInvalid: 'Enter a valid CNPJ with 14 digits',
      legalRepresentativeNameMaxLength:
        'Representative name must be at most 100 characters',
      legalRepresentativeDocumentInvalid: 'Enter a valid CPF with 11 digits',
      emailInvalid: 'Enter a valid email',
      phoneMaxLength: 'Phone must be at most 30 characters',
      instagramMaxLength: 'Instagram must be at most 30 characters',
    },
  },
  legalInfo: {
    title: 'Contract data',
    description: 'Client personal data used to fill in contracts.',
    fields: {
      cpf: 'CPF',
      rg: 'ID number',
      profession: 'Profession',
      maritalStatus: 'Marital status',
      nationality: 'Nationality',
    },
    save: 'Save contract data',
    toast: {
      saved: 'Contract data saved',
      error: 'Could not save the contract data. Please try again',
    },
    validation: {
      cpfInvalid: 'Enter a valid CPF with 11 digits',
      rgMaxLength: 'ID number must be at most 20 characters',
      professionMaxLength: 'Profession must be at most 100 characters',
      maritalStatusMaxLength: 'Marital status must be at most 50 characters',
      nationalityMaxLength: 'Nationality must be at most 50 characters',
    },
  },
  address: {
    title: 'Address',
    fields: {
      street: 'Street',
      number: 'Number',
      complement: 'Complement',
      neighborhood: 'Neighborhood',
      city: 'City',
      state: 'State',
      zipCode: 'ZIP code',
    },
  },
  variableSource: {
    LEAD: 'Client',
    LEAD_LEGAL: 'Contract data',
    CONTRACTOR: 'Contractor',
    SERVICE: 'Service',
    PAYMENT: 'Payment',
    ISSUANCE: 'Issuance',
    CUSTOM: 'Custom',
  },
  status: {
    DRAFT: 'Draft',
    ISSUED: 'Issued',
    SIGNED: 'Signed',
  },
  templates: {
    page: {
      title: 'Contract templates',
      search: 'Search template',
      emptyState: 'No contract template registered',
      emptyStateHint: 'Upload a .docx file to create your first template',
    },
    list: {
      variablesCount_one: '{{count}} variable',
      variablesCount_other: '{{count}} variables',
      active: 'Active',
      inactive: 'Inactive',
      more: 'More actions',
    },
    detail: {
      requirementsTitle: 'What this template needs',
      requirementsDescription:
        'Prepare this data before issuing a contract with this template.',
      requirementsEmpty: 'This template requires no mandatory data',
      optionalTitle: 'Optional data',
      noAnchorBadge: 'no excerpt linked in the document',
      missingAnchorsWarning:
        'The variables {{names}} have no linked excerpt and will not be filled in the document.',
      variablesTitle: 'Template variables',
      variablesEmpty: 'This template has no mapped variables',
      key: 'Key',
      label: 'Label',
      source: 'Source',
      required: 'Required',
      requiredYes: 'Yes',
      requiredNo: 'No',
      createdAt: 'Created at',
      status: 'Status',
      close: 'Close details',
    },
    actions: {
      detail: 'View details',
      edit: 'Edit template',
      delete: 'Delete template',
    },
    modal: {
      titleCreate: 'New contract template',
      titleEdit: 'Edit contract template',
      nameField: 'Template name',
      activeField: 'Active template',
      upload: {
        label: 'Template file (.docx)',
        hint: 'Only .docx files up to 5MB',
        selectFile: 'Select file',
        changeFile: 'Change file',
        uploading: 'Uploading file…',
        reading: 'Reading the document…',
        analyzing: 'The AI is analyzing the document…',
        analyzingHint:
          'Identifying the contract variables. This may take up to 20 seconds.',
        analysis: {
          title: 'Automatic analysis unavailable',
          emptyTitle: 'No text found in this document',
          manual:
            'The AI could not suggest the variables right now. Carry on with the manual mapping: select passages in the text to create the variables.',
          fallback_one:
            'We found {{count}} variable already written as a placeholder in the document. Review it and finish the mapping manually.',
          fallback_other:
            'We found {{count}} variables already written as placeholders in the document. Review them and finish the mapping manually.',
          success_one:
            'The AI suggested {{count}} variable. Review it before saving.',
          success_other:
            'The AI suggested {{count}} variables. Review them before saving.',
          suggest: 'Suggest variables with AI',
          reason: {
            NOT_CONFIGURED: 'AI analysis is unavailable for this account.',
            TIMEOUT: 'The analysis took too long to respond.',
            RATE_LIMITED: 'AI usage limit reached. Try again in a few minutes.',
            UNAVAILABLE: 'The AI service is unstable right now.',
            DOCUMENT_TOO_LONG:
              'This contract is too long for the automatic analysis.',
            INVALID_OUTPUT: 'The AI returned a response we could not use.',
            DOCUMENT_EMPTY:
              'There is no selectable text in this .docx. It may be a scanned document (image) or have its content only in the header and footer. Upload a file with text in the body.',
          },
        },
      },
      back: 'Back',
      next: 'Continue',
      save: 'Save template',
      cancel: 'Cancel',
    },
    variables: {
      title: 'Variables',
      description:
        'Review the variables and where they appear in the document. Select a snippet of text to tag a new occurrence.',
      empty: 'No mapped variables',
      add: 'Add variable',
      keyField: 'Key',
      labelField: 'Label',
      sourceField: 'Source',
      requiredField: 'Required',
      remove: 'Remove variable',
      anchorsCount_one: '{{count}} occurrence tagged',
      anchorsCount_other: '{{count}} occurrences tagged',
      notPositioned: 'Not positioned in the document',
      removeAnchor: 'Remove occurrence',
      locate: 'View in document',
      keyHint: 'The key is generated from the label and used as the marker.',
      validation: {
        keyRequired: 'Enter the variable key',
        keyDuplicated: 'Duplicated key',
        labelRequired: 'Enter the variable label',
      },
    },
    editor: {
      title: 'Map the variables in the document',
      instruction:
        'Select a snippet of text (e.g. the client name) and turn it into a variable.',
      simplifiedNotice: 'Simplified text preview',
      documentTab: 'Document',
      variablesTab: 'Variables',
      loadingDocument: 'Loading document…',
      emptyDocument: 'No text available for this template.',
      emptyDocumentHint:
        'If the file is scanned (an image) or has its content only in the header and footer, upload a .docx with text in the body.',
      selectionInvalid: 'Select a snippet within a single paragraph.',
      selectionTooShort:
        'Select a snippet with at least 2 characters and one letter or digit.',
      previewToggle: 'Preview filled',
      previewValues: {
        LEAD: 'Mary Smith',
        LEAD_LEGAL: '123.456.789-00',
        CONTRACTOR: 'Sample Studio Ltd.',
        SERVICE: 'Photo session — Gold Package',
        PAYMENT: '$2,500.00 in 3 installments',
        ISSUANCE: 'São Paulo, July 21, 2026',
      },
      popover: {
        title: 'Tag variable',
        context: 'Selected snippet',
        linkExisting: 'Link to an existing variable',
        createNew: 'Create a new variable',
        existingField: 'Variable',
        labelField: 'Label',
        sourceField: 'Source',
        requiredField: 'Required',
        keyField: 'Key (marker)',
        confirm: 'Confirm',
        cancel: 'Cancel',
        labelRequired: 'Enter the variable label',
        noVariables: 'No variables created yet',
        reviewTitle: 'Review excerpt',
        linkedTo: 'Linked to: {{label}}',
        removeAnchor: 'Remove excerpt',
        changeVariable: 'Change variable',
        applyToOthers_one: 'Apply to {{count}} other occurrence',
        applyToOthers_other: 'Apply to {{count}} other occurrences',
      },
    },
    confirmMissingAnchors: {
      title: 'Variables without a linked excerpt',
      text: 'Some required variables have no excerpt linked in the document.',
      secondaryText: "They won't be filled in issued contracts. Save anyway?",
      confirm: 'Save anyway',
      cancel: 'Review variables',
    },
    validation: {
      nameRequired: 'Enter the template name',
      nameMaxLength: 'Name must be at most 100 characters',
      fileRequired: 'Upload the template .docx file',
      noAnchors:
        'Mark at least one occurrence in the document before saving the template.',
    },
    delete: {
      title: 'Delete template',
      text: 'Are you sure you want to delete the template "{{name}}"?',
      secondaryText:
        'Contracts already issued with this template will not be changed.',
      confirm: 'Delete',
      cancel: 'Cancel',
    },
    toast: {
      created: 'Contract template created',
      updated: 'Contract template updated',
      deleted: 'Contract template deleted',
      error: {
        create: 'Could not create the template. Please try again',
        update: 'Could not update the template. Please try again',
        delete: 'Could not delete the template. Please try again',
      },
    },
    errors: {
      invalidFile: 'Only .docx files are accepted',
      fileTooLarge: 'File must be at most 5MB',
      uploadFailed: 'Could not upload the file. Please try again',
      unauthorized: "You don't have permission to access this page.",
      analyzeUnreachable:
        'We could not reach the server to suggest the variables. You can map them manually.',
      paragraphsUnreachable:
        'We could not read the document. Check your connection and upload the file again.',
    },
  },
  leadContracts: {
    title: 'Contracts',
    description: 'Contracts issued for this client.',
    issueButton: 'Issue contract',
    empty: 'No contract issued for this client',
    issuedAt: 'Issued on {{date}}',
    fallbackTemplateName: 'Contract',
    uploadingSigned: 'Uploading signed contract…',
    signedUpload: {
      placeholder: 'Drag the signed PDF here or click to select',
      hint: 'Only .pdf files up to 10 MB',
    },
    actions: {
      downloadDocx: 'Download DOCX',
      downloadSigned: 'Download signed',
      uploadSigned: 'Upload signed',
      delete: 'Delete contract',
      more: 'More actions',
    },
    delete: {
      title: 'Delete contract',
      text: 'Are you sure you want to delete this contract?',
      secondaryText: 'The generated files will no longer be available.',
      confirm: 'Delete',
      cancel: 'Cancel',
    },
    toast: {
      deleted: 'Contract deleted',
      deleteError: 'Could not delete the contract. Please try again',
      downloadError: 'Could not download the file. Please try again',
      signedUploaded: 'Signed contract uploaded',
      signedUploadError:
        'Could not upload the signed contract. Please try again',
    },
    errors: {
      invalidSignedFile: 'Only PDF files are accepted',
      signedFileTooLarge: 'File must be at most 10MB',
    },
  },
  issue: {
    modal: {
      title: 'Issue contract',
      templateField: 'Contract template',
      contractorField: 'Contractor',
      issuanceCityField: 'Issuance city',
      saleField: 'Sale',
      fillLegalTitle: 'Fill in the client data',
      fillLegalHint:
        'These fields are missing for this template. Saving stores them on the client record and reuses them on the next contracts.',
      fillLegalSave: 'Save and review',
      missingAddressHint:
        'The client address is incomplete. Fill it in the Contracts tab of the lead, under client data.',
      saleFieldLatest: 'Most recent sale',
      customValuesTitle: 'Contract fields',
      noActiveTemplates:
        'No active template available. Register a template on the Contracts page.',
      noContractors:
        'No contractor registered. Register a contractor in Settings.',
      next: 'Review data',
      back: 'Back',
      confirm: 'Issue contract',
      cancel: 'Cancel',
      issuing: 'Generating document…',
      issuingHint: 'This may take a few seconds. Do not close this window.',
      previewTitle: 'Data review',
      previewDescription:
        'Check the values that will be used in the contract before issuing.',
      missingTitle: 'Pending data',
      missingAnchorsWarning:
        'The variables {{names}} have no linked excerpt and will not be filled in the contract.',
      emptyValue: '—',
    },
    missingHint: {
      LEAD: 'Fill in on the client record',
      LEAD_LEGAL: "Fill in on the client's contract data",
      CONTRACTOR: 'Fill in on the contractor record',
      SERVICE: 'Fill in on the contract fields',
      PAYMENT: 'Fill in on the contract fields',
      ISSUANCE: 'Enter the issuance city',
      CUSTOM: 'Fill in on the contract fields',
    },
    validation: {
      templateRequired: 'Select a contract template',
      contractorRequired: 'Select a contractor',
    },
    toast: {
      legalInfoSaved: 'Client data saved',
      legalInfoError: 'Could not save the client data',
      issued: 'Contract issued',
      pdfFailed:
        'Contract issued, but the PDF generation failed. The DOCX is available for download.',
      error: 'Could not issue the contract. Please try again',
      previewError: 'Could not load the data review. Please try again',
    },
  },
  signature: {
    loading: 'Loading signature…',
    expiresAt: 'Expires on {{date}}',
    acceptedAt: 'Signed on {{date}}',
    status: {
      PENDING: 'Awaiting signature',
      ACCEPTED: 'Signed',
      EXPIRED: 'Expired',
      CANCELED: 'Canceled',
    },
    actions: {
      send: 'Send for signature',
      copyLink: 'Copy link',
      cancel: 'Cancel request',
    },
    toast: {
      linkCopied: 'Link copied',
      linkCopyError: 'Could not copy the link',
      canceled: 'Signature request canceled',
      cancelError: 'Could not cancel the request. Please try again',
    },
    cancel: {
      title: 'Cancel signature request',
      text: 'Are you sure you want to cancel this signature request?',
      secondaryText: 'The link sent to the signer will stop working.',
      confirm: 'Cancel request',
      cancel: 'Back',
    },
    modal: {
      title: 'Send for signature',
      description:
        'Generate an electronic signature link to send to the signer.',
      submit: 'Generate link',
      cancel: 'Cancel',
      fields: {
        signerName: 'Signer name',
        signerEmail: 'Signer email (optional)',
        signerDocument: 'Signer document (optional)',
      },
      validation: {
        signerNameRequired: 'Enter the signer name',
        signerNameMaxLength: 'The name must be at most 256 characters',
        signerEmailInvalid: 'Enter a valid email',
        signerDocumentInvalid: 'Enter a valid document',
      },
      toast: {
        created: 'Signature link generated',
        createError: 'Could not generate the signature link. Please try again',
        linkCopied: 'Link copied',
        linkCopyError: 'Could not copy the link',
      },
      success: {
        title: 'Signature link generated',
        hint: 'Copy the link below and send it to the signer.',
        copy: 'Copy link',
        validity: 'The link is valid for 30 days.',
        done: 'Done',
      },
    },
  },
};

export default contractTranslateEN;
